/**
 * useNewOrderAlert — fires a "got a new order!" alert when a fresh order lands.
 *
 * How it works:
 *   1. On the first orders snapshot it *seeds* the set of seen IDs and stays
 *      silent — so opening the board never alerts for orders already on screen.
 *   2. After that, any order ID we haven't seen before (and that was created
 *      recently) triggers, once:
 *        • a distinct rising chime (playNewOrderChime — different from the
 *          late-order alarm), and
 *        • a local browser notification via the service worker, which shows
 *          even when the dashboard tab is in the background, and
 *        • an FCM web-push through /api/send-notification, so the alert also
 *          reaches the device when the browser/tab is fully closed.
 *
 * Controls (passed in): `enabled` master switch and `muted` (push still fires,
 * chime is suppressed). Both are owned by KdsBoardTab and persisted there.
 */
import { useEffect, useRef } from 'react';
import { playNewOrderChime } from '../ticketUtils';
import { isFreshNewOrder } from '../ticketTimingUtils';
import { getCachedFCMToken } from '../../../utils/fcmUtils';
import { NOTIFY_API_BASE } from '../ticketConstants';

// Only alert for orders created within this window — guards against an old
// order resurfacing in a poll and triggering a false "new order" chime.
const FRESH_WINDOW_MS = 20 * 60 * 1000;

function orderSummary(o) {
  const label =
    (o.ticketName && o.ticketName.trim()) ||
    (o.customerName && o.customerName.trim()) ||
    ('#' + (o.shortId || (o.id || '').slice(-4)));
  const itemCount = (o.items || []).reduce((s, it) => s + (Number(it.qty) || 1), 0);
  const itemNames = (o.items || [])
    .map(it => (it.qty > 1 ? `${it.qty}× ${it.name}` : it.name))
    .filter(Boolean)
    .slice(0, 6)
    .join(', ');
  const total = typeof o.total === 'number' ? ` · $${o.total.toFixed(2)}` : '';
  return {
    label,
    itemCount,
    title: `🔔 New order — ${label}`,
    body: `${itemCount} item${itemCount === 1 ? '' : 's'}${total}${itemNames ? `\n${itemNames}` : ''}`,
  };
}

function showLocalNotification(title, body, tag) {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const opts = {
      body,
      tag,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      requireInteraction: false,
      // vibrate is ignored on desktop but helps on mobile/PWA installs
      vibrate: [120, 60, 120],
      data: { url: window.location.href },
    };
    if (navigator.serviceWorker && navigator.serviceWorker.ready) {
      navigator.serviceWorker.ready
        .then(reg => reg.showNotification(title, opts))
        .catch(() => { try { new Notification(title, opts); } catch {} });
    } else {
      new Notification(title, opts);
    }
  } catch { /* notifications are best-effort */ }
}

function sendFcmPush(orderId, title, body) {
  const token = getCachedFCMToken();
  if (!token) return; // FCM not initialised on this device — local notif still fired
  fetch(`${NOTIFY_API_BASE}/api/send-notification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      alertKey: `new_order_${orderId}`,   // unique per order so it's never rate-limited away
      title,
      body: body.replace(/\n/g, ' · '),
      severity: 'high',
      channels: ['fcm'],
      fcmTokens: [token],
    }),
  }).catch(() => {});
}

export function useNewOrderAlert(orders, { enabled = true, muted = false } = {}) {
  const seenRef = useRef(null); // null until first snapshot seeds it
  // Session start. Orders created before this are backlog and must never alert,
  // even if the first snapshot was empty (cold open with no cache) and the real
  // order list only arrives on a later poll.
  const mountRef = useRef(Date.now());
  // Keep latest control values without re-running the detection effect.
  const ctrlRef = useRef({ enabled, muted });
  useEffect(() => { ctrlRef.current = { enabled, muted }; }, [enabled, muted]);

  useEffect(() => {
    if (!Array.isArray(orders)) return;
    const ids = orders.map(o => o.id).filter(Boolean);

    // First snapshot → remember everything, alert for nothing.
    if (seenRef.current === null) {
      seenRef.current = new Set(ids);
      return;
    }

    const seen = seenRef.current;
    const nowMs = Date.now();
    const fresh = [];
    for (const o of orders) {
      if (!o.id || seen.has(o.id)) continue;
      seen.add(o.id); // mark immediately so we never double-fire
      if (isFreshNewOrder(o, { mountMs: mountRef.current, nowMs, freshWindowMs: FRESH_WINDOW_MS })) {
        fresh.push(o);
      }
    }

    if (fresh.length === 0) return;
    const { enabled: isOn, muted: isMuted } = ctrlRef.current;
    if (!isOn) return;

    // One chime per batch (avoids a pile-up if several arrive at once).
    if (!isMuted) playNewOrderChime();

    fresh.forEach(o => {
      const { title, body } = orderSummary(o);
      showLocalNotification(title, body, `new_order_${o.id}`);
      sendFcmPush(o.id, title, body);
    });
  }, [orders]);
}
